import {createSelector} from "@reduxjs/toolkit";

import type {MarketAndLendingValueSpec} from "@reva/shared/number-format";
import type {RevaSpecObjectInputRow} from "@reva/shared/reva-spec";
import type {
    RevaIndicationEndpoint,
    RevaSpec,
    RevaSpecOutputRef,
    RevaSpecOutputRow,
} from "@reva/shared/spec-conversion";
import {
    findOutputParam,
    findPageOutputParam,
    indexModelProperties,
    indexRangesByApiParam,
} from "@reva/shared/spec-conversion";

import {currySelector, selectArg, selectArg1, selectArg2} from "../util/selectors";
import {selectSegment} from "./objectParametersSlice";
import {
    selectComparativeValueSpec,
    selectLocationInfoPriceIndexSpec,
    selectLocationInfoStandardGroundValueSpec,
    selectQualityFurnishingsSpec,
    selectSpec,
    selectSpecApiParamInputs,
    selectSpecApiParamOutputs,
    selectSpecAvailable,
} from "./specSlice";
import {selectCapabilitiesSettled} from "./wmtsSlice";

export const selectStaticDataLoaded = createSelector(
    [selectSpecAvailable, selectCapabilitiesSettled],
    (specAvailable, capabilitiesSettled) => specAvailable && capabilitiesSettled,
);

export const selectSingleApiParamOutputDef = createSelector(
    [selectArg<string>, selectSpecApiParamOutputs],
    (parameter, spec) => spec?.[parameter] ?? null,
);
export const selectSingleApiParamOutputDefCurried = currySelector(selectSingleApiParamOutputDef);

/** Input-Parameter-Definitionen des aktuellen Segments, nach Parametername. */
export const selectApiInputParamsForCurrentSegment = createSelector(
    [selectSegment, selectSpecApiParamInputs],
    (segment, apiInputParams) => apiInputParams?.[segment] ?? {},
);

export const selectApiInputParamForCurrentSegment = createSelector(
    [selectArg<string>, selectApiInputParamsForCurrentSegment],
    (parameter, apiInputParams) => apiInputParams[parameter] ?? null,
);
export const selectApiInputParamForCurrentSegmentCurried = currySelector(selectApiInputParamForCurrentSegment);

const selectRangesByApiParam = createSelector([selectSpec], indexRangesByApiParam);

export const selectRangesForApiParam = createSelector(
    [selectArg<string>, selectRangesByApiParam],
    (parameter, rangesByApiParam) => rangesByApiParam[parameter] ?? null,
);
export const selectRangesForApiParamCurried = currySelector(selectRangesForApiParam);

export const selectPriceIndexSpecPropertiesModel = createSelector(
    [selectLocationInfoPriceIndexSpec],
    (priceIndexSpec) => {
        const priceIndexParam = findOutputParam(priceIndexSpec, "price_index");
        return indexModelProperties(priceIndexParam);
    },
);

export const selectPriceIndexSpecValuePropertiesModel = createSelector(
    [selectPriceIndexSpecPropertiesModel],
    (priceIndexSpecPropertiesModel) => {
        const indexProperty = priceIndexSpecPropertiesModel?.index;
        return indexModelProperties(indexProperty);
    },
);

export const selectComparableValueSpecComparableDetailsParameter = createSelector(
    [selectComparativeValueSpec],
    (comparativeValueSpec) => {
        return findOutputParam(comparativeValueSpec, "comparable_details");
    },
);

export const selectStandardGroundValueSpecModel = createSelector(
    [selectLocationInfoStandardGroundValueSpec],
    (standardGroundInfoSpec) => {
        const listParam = findOutputParam(standardGroundInfoSpec, "list");
        return indexModelProperties(listParam);
    },
);

/** German craft-matrix key (e.g. "daecher") → its model property (nameDe/nameEn etc.), see #19405. */
export const selectQualityFurnishingsCraftsMatrixModel = createSelector(
    [selectQualityFurnishingsSpec],
    (qualityFurnishingsSpec) => {
        const nhkCraftsMatrixParam = findOutputParam(qualityFurnishingsSpec, "nhk_crafts_matrix");
        return indexModelProperties(nhkCraftsMatrixParam);
    },
);

/**
 * Craft (`prop_craft_x`) → German `nhk_crafts_matrix` key (z.B. "daecher"): die AVM-Spec verknüpft
 * beide nicht direkt, daher Abgleich über den deutschen Namen statt einer fest verdrahteten Map (#19405).
 */
export const selectQualityFurnishingsCraftToMatrixKey = createSelector(
    [selectArg<string>, selectSpec, selectQualityFurnishingsCraftsMatrixModel],
    (segment, spec, craftsMatrixModel) => {
        const inputParams = spec?.apiParams?.QUALITY_FURNISHINGS?.[segment] ?? {};
        const matrixKeyByNameDe: Record<string, string> = {};
        for (const property of Object.values(craftsMatrixModel)) {
            if (property.nameDe) matrixKeyByNameDe[property.nameDe] = property.property;
        }
        const craftToMatrixKey: Record<string, string> = {};
        for (const [weightsParam, paramDef] of Object.entries(inputParams)) {
            const craftNameDe = paramDef.nameDe?.replace(/^Stufengewichte für /, "");
            const matrixKey = craftNameDe != null ? matrixKeyByNameDe[craftNameDe] : undefined;
            if (matrixKey) craftToMatrixKey[weightsParam.replace(/_weights$/, "")] = matrixKey;
        }
        return craftToMatrixKey;
    },
);
export const selectQualityFurnishingsCraftToMatrixKeyCurried = currySelector(selectQualityFurnishingsCraftToMatrixKey);

/**
 * Maps a ValuesIconDefault tooltip layer to the object-input spec page it draws its rows from - only
 * for layers backed by a distinct spec. */
const VALUE_TOOLTIP_SUBTAB_SPEC_PAGES: Record<string, string> = {
    register: "object/register",
    energy: "object/energy",
    idpEnergyCertificate: "object/energy",
    plot: "object/groundvalue",
    inspectionDerived: "object/inspection",
};

const selectObjectInputPageParameters = (specData: RevaSpec | null | undefined, pageKey: string): Set<string> =>
    new Set(
        (specData?.objectInputs || [])
            .filter((p) => p.key === pageKey)
            .flatMap((p) => p.columns)
            .flatMap((c) => c.blocks)
            .flatMap((b) => b.rows)
            .map((r) => r.parameter),
    );

/**
 * Parameters with an Overview (AVM) row in the current segment, or null while the spec has no Overview page.
 * Only there can a parameter conflict be resolved; a conflict on any other parameter must not block finalizing
 * (#19774).
 */
export const selectOverviewAvmRowParameters = createSelector([selectSpec, selectSegment], (specData, segment) => {
    const overviewPages = (specData?.objectInputs || []).filter((p) => p.key === "object/overview");
    if (!overviewPages.length) return null;
    const isVisible = (onlyVisibleInSegments?: string[] | null) =>
        !onlyVisibleInSegments?.length || (segment != null && onlyVisibleInSegments.includes(segment));
    return new Set(
        overviewPages
            .flatMap((p) => p.columns)
            .flatMap((c) => c.blocks)
            .filter((b) => isVisible(b.onlyVisibleInSegments))
            .flatMap((b) => b.rows)
            .filter((r) => r.parameterType === "AVM" && isVisible(r.onlyVisibleInSegments))
            .map((r) => r.parameter),
    );
});

/** RegisterAssetsInput (TabRegister/AssetComponents) writes these as REGISTER-layer totals, not as object/register spec rows. */
const REGISTER_EXTRA_TOOLTIP_PARAMS = ["space_plot", "ownership_share"];

/** ObjectInputModernizationDegree writes this as an INSPECTION_DERIVED value, not as an object/inspection spec row. */
const INSPECTION_DERIVED_EXTRA_TOOLTIP_PARAMS = ["degree_modernization_category"];

/**
 * For each ValuesIconDefault tooltip layer with a dedicated object-input spec page, the set of parameters
 * listed on that page - so a layer can be hidden from a parameter's tooltip when its page's spec never lists
 * that parameter, rather than only checking whether the layer has a value for *some* parameter.
 */
export const selectValueTooltipSubtabSpecParams = createSelector([selectSpec], (specData) => {
    const result = Object.fromEntries(
        Object.entries(VALUE_TOOLTIP_SUBTAB_SPEC_PAGES).map(([key, pageKey]) => [
            key,
            selectObjectInputPageParameters(specData, pageKey),
        ]),
    );
    REGISTER_EXTRA_TOOLTIP_PARAMS.forEach((parameter) => {
        result.register.add(parameter);
    });
    INSPECTION_DERIVED_EXTRA_TOOLTIP_PARAMS.forEach((parameter) => {
        result.inspectionDerived.add(parameter);
    });
    return result;
});

export const selectStandardGroundValueInputs = createSelector([selectSpec], (specData) => {
    return (specData?.objectInputs || [])
        .filter((i) => i.key === "object/groundvalue")
        .flatMap((i) => i.columns)
        .flatMap((i) => i.blocks)
        .flatMap((i) => i.rows)
        .filter((r) => r.parameter.startsWith("standard_ground_value"))
        .reduce<Record<string, RevaSpecObjectInputRow>>((acc, r) => {
            acc[r.parameter] = r;
            return acc;
        }, {});
});

export const selectStandardGroundValueOutputs = createSelector([selectSpec], (specData) => {
    if (!specData?.pages) return null;
    return Object.values(specData.pages)
        .flatMap((p) => p.rows.filter((r) => r.group === "bodenwert"))
        .filter((r) => r.factor?.field?.startsWith("standard_ground_value"))
        .reduce<Record<string, RevaSpecOutputRow>>((acc, r) => {
            if (r.factor?.field) {
                acc[r.factor.field] = r;
            }
            return acc;
        }, {});
});

/** Value spec describing the format of a market/lending value and its per-sqm variants. */
export interface ValueInfoSpec extends MarketAndLendingValueSpec {
    sectionII?: RevaSpecOutputRef | null | undefined;
}

const selectValueAndLendingValueSpec = createSelector(
    [selectSpec, selectArg1<RevaIndicationEndpoint>, selectArg2<boolean>],
    (specData, endpoint, useRent = false): ValueInfoSpec | null => {
        if (!specData?.pages) return null;
        const valueSpec = findPageOutputParam(specData, endpoint, useRent ? "market_rent" : endpoint.toLowerCase());
        const valueSqmSpec =
            findPageOutputParam(
                specData,
                endpoint,
                useRent ? "market_rent_per_sqm" : `${endpoint.toLowerCase()}_per_sqm`,
            ) ?? findPageOutputParam(specData, endpoint, `${endpoint.toLowerCase()}_per_area`);
        const sectionIIValueSpec = findPageOutputParam(specData, "COMPARATIVE_VALUE", "section_ii_sum");
        return {
            market: valueSpec?.market_value,
            marketSqm: valueSqmSpec?.market_value,
            lending: valueSpec?.lending_value,
            lendingSqm: valueSqmSpec?.lending_value,
            sectionII: sectionIIValueSpec?.market_value,
        };
    },
);

export const selectValueAndLendingValueSpecCurried = currySelector(selectValueAndLendingValueSpec);
